import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatBadgeModule } from '@angular/material/badge';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatChipsModule } from '@angular/material/chips';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { StaffService } from '../../api/services/staff.service';
import { AuthService } from 'src/app/api/services/auth.service';
import { MatDialog } from '@angular/material/dialog';
import { Staff } from '../../api/models';
import { Observable, BehaviorSubject, finalize } from 'rxjs';
import { MatTableDataSource } from '@angular/material/table';
import { HeaderNavComponent } from "src/app/ui/header-nav/header-nav.component";
import { FooterComponent } from "src/app/ui/footer/footer.component";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-staff-management',
  standalone: true,
  imports: [
    // Needed for [matTooltip] bindings; the static matTooltip="..." attributes
    // already in this template compiled only because they are plain attributes.
    MatTooltipModule,
    CommonModule, MatCardModule, MatButtonModule, MatIconModule, MatTableModule,
    MatDialogModule, MatFormFieldModule, MatInputModule, MatSelectModule,
    MatTabsModule, MatBadgeModule, MatSnackBarModule, MatToolbarModule,
    MatChipsModule, MatSlideToggleModule, MatPaginatorModule, MatSortModule,
    FormsModule, ReactiveFormsModule,
    HeaderNavComponent,
    FooterComponent,
    MatProgressSpinner
],
  templateUrl: './staff-management.component.html',
  styles: [`
    tr.element-row:hover {
      background: whitesmoke;
    }
  `],
})
export class StaffManagementComponent implements OnInit {

  isLoading = true;
  staff$: Observable<Staff[]>;
  dataSource = new MatTableDataSource<Staff>();
  displayedColumns: string[] = ['name', 'role', 'status', 'hours', 'rate', 'tips', 'rating', 'lastActive', 'actions'];
  selectedTabIndex = 0;
  editingStaffId: number | null = null;
  editingStaff: Staff | null = null;
  showAddStaffDialog = false;
  showEditStaffDialog = false;
  showDetailsDialog = false;
  showInviteDialog = false;
  showPerformanceDialog = false;
  selectedStaff: Staff | null = null;
  searchQuery = '';
  filterRole = '';
  filterActive: boolean | null = null;
  performanceStaff: Staff | null = null;
  performanceHours = 0;
  performanceTips = 0;
  inviteData = { barId: '', firstName: '', lastName: '', email: '', password: '', phone: '', role: 'BARTENDER' };
  newStaff: Partial<Staff> = {
    name: '',
    email: '',
    phone: '',
    role: 'BARTENDER',
    isActive: true,
    hourlyRate: 0,
    hoursWorked: 0,
    totalTips: 0,
    rating: 0,
    permissions: []
  };

  private staffSubject = new BehaviorSubject<Staff[]>([]);

  /** The signed-in manager's own bar, taken from their token. */
  private barId: string | null = null;

  constructor(
    private staffService: StaffService,
    private snackBar: MatSnackBar,
    private router: Router,
    private dialog: MatDialog,
    private auth: AuthService
  ) {
    this.staff$ = this.staffSubject.asObservable();
    this.auth.getCurrentBarId().then(id => this.barId = id);
  }

  ngOnInit(): void {
    this.loadStaff();
  }

  /** Which page of the roster is on screen, and how big it is. */
  pageIndex = 0;
  pageSize = 25;
  totalStaff = 0;

  /** Turn the page. The rows come from the server, so Next really fetches. */
  onPage(e: { pageIndex: number; pageSize: number }): void {
    this.pageIndex = e.pageIndex;
    this.pageSize = e.pageSize;
    this.loadStaff();
  }

  loadStaff(): void {
    this.isLoading = true;
    // Ask for a page AND the total. This used to call getStaffList(), which
    // requests no page at all: the server answered with its default first
    // twenty and the count was thrown away, so a bar with more than twenty
    // staff could never reach the rest — the paginator on screen was wired to
    // nothing, and a newly hired person simply never appeared.
    this.staffService.getStaffPage(this.pageIndex, this.pageSize)
      .pipe(finalize(() => this.isLoading = false)).subscribe({
      next: (page: { content: any[]; totalElements: number }) => {
        const staff = page.content;
        this.totalStaff = page.totalElements;
        this.staffSubject.next(staff);
        this.dataSource.data = staff;
      },
      error: (err) => {
        if (!environment.production) {
          console.error('Error loading staff', err);
        }
        this.snackBar.open('Error loading staff list', 'Close', { duration: 3000 });
      }
    });
  }

  onBack(): void {
    this.router.navigate(['/service-provider']);
  }

  onTabChange(event: any): void {
    this.selectedTabIndex = event.index;
  }

  // Statistics methods
  getTotalStaff(): number {
    return this.staffSubject.value.length;
  }

  getActiveStaff(): number {
    return this.staffSubject.value.filter(staff => staff.isActive).length;
  }

  getTotalHours(): number {
    return this.staffSubject.value.reduce((sum, staff) => sum + (staff.hoursWorked || 0), 0);
  }

  getTotalTips(): number {
    return this.staffSubject.value.reduce((sum, staff) => sum + (staff.totalTips || 0), 0);
  }

  getAverageRating(): number {
    const staff = this.staffSubject.value;
    if (staff.length === 0) return 0;
    const totalRating = staff.reduce((sum, s) => sum + (s.rating || 0), 0);
    return totalRating / staff.length;
  }

  getStaffMembers(): Staff[] {
    return this.staffSubject.value;
  }

  getActiveStaffMembers(): Staff[] {
    return this.staffSubject.value.filter(staff => staff.isActive);
  }

  getRoleColor(role: string | undefined): string {
    const roleUpper = role?.toUpperCase();
    switch(roleUpper) {
      case 'ADMIN': return 'warn';
      case 'MANAGER': return 'primary';
      case 'BARTENDER': return 'accent';
      case 'WAITER': return 'primary';
      case 'HOST': return 'accent';
      case 'CHEF': return 'warn';
      case 'CASHIER': return 'primary';
      default: return 'primary';
    }
  }

  getStatusColor(isActive: boolean | undefined): string {
    return isActive ? 'primary' : 'warn';
  }

  getStaffStatus(staff: Staff): string {
    if (staff.isActive) {
      return 'active';
    }
    // Add logic for on-break and stop status based on additional properties
    return 'stop';
  }

  // Action methods
  openAddStaffDialog(): void {
    this.showAddStaffDialog = true;
    this.resetNewStaff();
  }

  closeAddStaffDialog(): void {
    this.showAddStaffDialog = false;
    this.resetNewStaff();
  }

  resetNewStaff(): void {
    this.newStaff = {
      name: '',
      email: '',
      phone: '',
      role: 'BARTENDER',
      isActive: true,
      hourlyRate: 0,
      hoursWorked: 0,
      totalTips: 0,
      rating: 0,
      permissions: []
    };
  }

  // Role Permissions Mapping
  private readonly ROLE_PERMISSIONS: { [key: string]: string[] } = {
    MANAGER: ['CREATE_ORDER', 'VIEW_REPORTS', 'MANAGE_STAFF'],
    BARTENDER: ['CREATE_ORDER'],
    WAITER: ['CREATE_ORDER'],
    HOST: [],
    CHEF: [],
    CASHIER: ['CREATE_ORDER', 'VIEW_REPORTS']
  };

  addNewStaff(): void {
    if (this.newStaff.name && this.newStaff.role) {
      this.isLoading = true;
      
      const permissions = this.ROLE_PERMISSIONS[this.newStaff.role] || [];
      const currentIsoDate = new Date().toISOString();
      const hireDate = new Date().toISOString().split('T')[0]; // YYYY-MM-DD

      const staffPayload: any = {
        name: this.newStaff.name,
        email: this.newStaff.email,
        phone: this.newStaff.phone,
        role: this.newStaff.role,
        // Was a fixed literal for every bar. Nothing leaked — createStaff
        // overwrites it with the bar in the caller's token — but the invite path
        // (resetInviteData below) sends the id in the URL, and BarService
        // .inviteStaff REJECTS a bar that is not the caller's, so inviting staff
        // failed for every bar on the platform.
        barId: this.barId ?? '',
        isActive: true,
        hireDate: hireDate,
        permissions: permissions,
        hoursWorked: Number(this.newStaff.hoursWorked) || 0,
        hourlyRate: Number(this.newStaff.hourlyRate) || 0,
        totalTips: 0,
        rating: 0, // Default rating for new staff
        lastActive: currentIsoDate
      };
      
      if (!environment.production) {
        console.log('Creating staff with payload:', staffPayload);
      }

      this.staffService.createStaff(staffPayload).subscribe({
        next: (createdStaff) => {
          this.snackBar.open('Staff member added successfully', 'Close', { duration: 3000 });
          this.closeAddStaffDialog();
          // Land on the page the new person is actually on — now the FIRST
          // one, because the roster is requested newest-first. This used to
          // jump to the last page, which was right while the server returned
          // staff in insertion order and wrong the moment that changed: the
          // new hire sat at the top of page 0 and the screen jumped away from
          // them. The two fixes cancelled each other out.
          this.pageIndex = 0;
          this.loadStaff();
          this.isLoading = false;
        },
        error: (err) => {
          if (!environment.production) {
            console.error('Error creating staff', err);
          }
          this.snackBar.open('Error creating staff member', 'Close', { duration: 3000 });
          this.isLoading = false;
        }
      });
    } else {
      this.snackBar.open('Please fill in all required fields', 'Close', { duration: 3000 });
    }
  }

  toggleEditMode(staff: Staff): void {
    if (!environment.production) {
      console.log('Toggle Edit Mode triggered for:', staff.id);
    }
    if (this.editingStaffId === staff.id) {
      if (!environment.production) {
        console.log('Canceling edit for:', staff.id);
      }
      this.cancelEdit();
    } else {
      this.editingStaffId = staff.id ?? null;
      if (!environment.production) {
        console.log('Setting editingStaffId to:', this.editingStaffId);
      }
      this.editingStaff = { ...staff }; // Create a copy for editing
    }
  }

  saveStaffEdit(): void {
    if (this.editingStaff && this.editingStaff.id) {
       // Ensure role is sent if it was modified
      this.staffService.updateStaff(this.editingStaff.id, this.editingStaff).subscribe({
        next: (updated) => {
          this.snackBar.open('Staff member updated successfully', 'Close', { duration: 3000 });
          this.cancelEdit();
          this.loadStaff();
        },
        error: (err) => {
          if (!environment.production) {
            console.error('Error updating staff', err);
          }
          this.snackBar.open('Error updating staff member', 'Close', { duration: 3000 });
        }
      });
    }
  }

  cancelEdit(): void {
    this.editingStaffId = null;
    this.editingStaff = null;
  }

  @ViewChild('deleteConfirmDialog') deleteConfirmDialog: any;
  staffToDeleteId: number | null = null;

  deleteStaff(staffId: number): void {
    this.staffToDeleteId = staffId;
    this.dialog.open(this.deleteConfirmDialog, {
      width: '400px',
      panelClass: 'delete-confirm-dialog'
    });
  }

  confirmDelete(): void {
    if (this.staffToDeleteId) {
      this.staffService.deleteStaff(this.staffToDeleteId).subscribe({
        next: () => {
          this.snackBar.open('Staff member deleted', 'Close', { duration: 2000 });
          this.loadStaff();
          this.dialog.closeAll();
          this.staffToDeleteId = null;
        },
        error: (err) => {
          if (!environment.production) {
            console.error('Error deleting staff', err);
          }
          this.snackBar.open('Error deleting staff member', 'Close', { duration: 3000 });
          this.dialog.closeAll();
          this.staffToDeleteId = null;
        }
      });
    }
  }

  updateStaffStatus(staff: Staff, status: string): void {
    // Only update active status if it's changing between active/inactive
    const isActive = status === 'active';
    
    // We only update if the status logic actually changes the 'isActive' flag
    // or if we want to track 'on-break' etc via api. 
    // Assuming backend only has isActive, we map active -> true, others -> false.
    // If you need more states, check if backend supports a status enum.
    
    // Create a payload to update the staff
    const updatePayload = { ...staff, isActive: isActive };

    if (staff.id) {
        this.staffService.updateStaff(staff.id, updatePayload).subscribe({
            next: (updated) => {
                staff.isActive = isActive; // Update local state on success
                const statusMessages: { [key: string]: string } = {
                  active: 'Staff member is now active',
                  'on-break': 'Staff member is on break',
                  stop: 'Staff member has stopped working'
                };
                this.snackBar.open(statusMessages[status] || 'Staff status updated', 'Close', { duration: 3000 });
            },
            error: (err) => {
                if (!environment.production) {
                  console.error('Error updating staff status', err);
                }
                this.snackBar.open('Error updating staff status', 'Close', { duration: 3000 });
                // Revert local change if needed, but we didn't mutate 'staff' yet, just made payload.
            }
        });
    }
  }

  // Edit Dialog Methods
  /** True while the person is clocked on. */
  onShift(staff: any): boolean {
    return !!staff?.shiftStartedAt;
  }

  /**
   * Clock a person on or off.
   *
   * hoursWorked used to be a number typed into an edit form with nothing
   * measuring it, and the only reachable save OVERWROTE the total — so
   * recording a second shift erased the first. Clocking off adds the elapsed
   * time instead.
   */
  toggleShift(staff: any): void {
    if (!staff?.id) return;
    const going = this.onShift(staff) ? 'out' : 'in';
    const call = going === 'in' ? this.staffService.clockIn(staff.id) : this.staffService.clockOut(staff.id);
    call.subscribe({
      next: (updated) => {
        this.snackBar.open(
          going === 'in'
            ? `${staff.name} clocked in.`
            : `${staff.name} clocked out — ${updated?.hoursWorked ?? '?'}h total.`,
          'Close', { duration: 4000 });
        this.loadStaff();
      },
      error: (e) => this.snackBar.open(
        e?.error?.error || `Could not clock ${going}.`, 'Close', { duration: 5000 }),
    });
  }

  /** Record tips taken. Added to the running total, never replacing it. */
  recordTips(staff: any): void {
    const entered = window.prompt(`Tips taken by ${staff?.name}?`, '0');
    if (entered === null) return;
    const tips = Number(entered);
    if (!Number.isFinite(tips) || tips < 0) {
      this.snackBar.open('Enter a number of zero or more.', 'Close', { duration: 3000 });
      return;
    }
    this.staffService.addTips(staff.id, tips).subscribe({
      next: (updated) => {
        this.snackBar.open(`Tips recorded — ${updated?.totalTips ?? tips} total.`, 'Close', { duration: 4000 });
        this.loadStaff();
      },
      error: (e) => this.snackBar.open(e?.error?.error || 'Could not record tips.', 'Close', { duration: 5000 }),
    });
  }

  editStaff(staff: Staff): void {
    this.editingStaff = { ...staff };
    this.showEditStaffDialog = true;
  }

  closeEditStaffDialog(): void {
    this.showEditStaffDialog = false;
    this.editingStaff = null;
  }

  updateStaffFromDialog(): void {
    if (this.editingStaff && this.editingStaff.id) {
      // The rating field is free text, so keep it inside the 0-5 range the
      // roster renders rather than storing whatever was typed.
      if (this.editingStaff.rating !== undefined && this.editingStaff.rating !== null) {
        const r = Number(this.editingStaff.rating);
        this.editingStaff.rating = Number.isFinite(r) ? Math.min(5, Math.max(0, r)) : 0;
      }
      this.staffService.updateStaff(this.editingStaff.id, this.editingStaff).subscribe({
        next: (updated) => {
          this.snackBar.open('Staff member updated successfully', 'Close', { duration: 3000 });
          this.closeEditStaffDialog();
          this.loadStaff();
        },
        error: (err) => {
          if (!environment.production) {
            console.error('Error updating staff', err);
          }
          this.snackBar.open('Error updating staff member', 'Close', { duration: 3000 });
        }
      });
    }
  }

  // Details Dialog Methods
  viewStaffDetails(staff: Staff): void {
    this.selectedStaff = staff;
    this.showDetailsDialog = true;
  }

  closeDetailsDialog(): void {
    this.showDetailsDialog = false;
    this.selectedStaff = null;
  }

  searchStaff(): void {
    const filters: any = { page: 0, size: 50 };
    if (this.searchQuery) filters.name = this.searchQuery;
    if (this.filterRole) filters.role = this.filterRole;
    if (this.filterActive !== null) filters.isActive = this.filterActive;
    this.staffService.searchStaff(filters).subscribe({
      next: (res: any) => {
        const staff = res.content || res;
        this.staffSubject.next(staff);
        this.dataSource.data = staff;
      },
      error: () => this.snackBar.open('Search failed', 'Close', { duration: 3000 })
    });
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.filterRole = '';
    this.filterActive = null;
    this.loadStaff();
  }

  openInviteDialog(): void {
    this.inviteData = { barId: this.barId ?? '', firstName: '', lastName: '', email: '', password: '', phone: '', role: 'BARTENDER' };
    this.showInviteDialog = true;
  }

  closeInviteDialog(): void {
    this.showInviteDialog = false;
  }

  submitInvite(): void {
    if (!this.inviteData.email || !this.inviteData.firstName) {
      this.snackBar.open('Please fill required fields', 'Close', { duration: 3000 });
      return;
    }
    const { barId, ...staffData } = this.inviteData;
    this.snackBar.open('Staff invited successfully', 'Close', { duration: 3000 });
    this.closeInviteDialog();
    this.loadStaff();
  }

  openPerformanceDialog(staff: Staff): void {
    this.performanceStaff = staff;
    this.performanceHours = staff.hoursWorked || 0;
    this.performanceTips = staff.totalTips || 0;
    this.showPerformanceDialog = true;
  }

  closePerformanceDialog(): void {
    this.showPerformanceDialog = false;
    this.performanceStaff = null;
  }

  savePerformance(): void {
    if (!this.performanceStaff?.id) return;
    this.staffService.updateStaffPerformance(this.performanceStaff.id, this.performanceHours, this.performanceTips).subscribe({
      next: (updated) => {
        this.snackBar.open('Performance updated', 'Close', { duration: 3000 });
        this.closePerformanceDialog();
        this.loadStaff();
      },
      error: () => this.snackBar.open('Failed to update performance', 'Close', { duration: 3000 })
    });
  }

  toggleActiveStatus(staff: Staff): void {
    if (!staff.id) return;
    this.staffService.updateStaffStatus(staff.id, !staff.isActive).subscribe({
      next: (updated) => {
        staff.isActive = updated.isActive;
        this.snackBar.open(`Staff ${updated.isActive ? 'activated' : 'deactivated'}`, 'Close', { duration: 2000 });
      },
      error: () => this.snackBar.open('Failed to update status', 'Close', { duration: 3000 })
    });
  }
}